import { useParams, Link } from "react-router-dom";
import { useLang } from "@/i18n/LanguageContext";
import { ArrowLeft, ExternalLink, Calendar, Building2, MapPin, AlertTriangle } from "lucide-react";
import HtmlText from "@/components/HtmlText";
import SmartImage from "@/components/SmartImage";
import SeoHead, { SITE_URL } from "@/components/SeoHead";
import {
  buildArticleSchema,
  buildJobPostingSchema,
  clampText,
} from "@/lib/schema";
import { TechChip } from "@/lib/tech";
import { TagChip } from "@/lib/tags";
import LinkedKeywords from "@/components/LinkedKeywords";
import ArticleLangNote from "@/components/ArticleLangNote";
import { resolveImg, pickEntityImage } from "@/lib/imagePicker";
import { useArticle, articleSeoProps, useArticleLangRoutes } from "@/lib/useArticle";
import { jobLabel, jobBadges, hasSalary } from "@/lib/jobLabels";

export const NewsDetail = () => {
  const { id } = useParams();
  const { t, lang } = useLang();
  const { item, err } = useArticle("news", id);
  useArticleLangRoutes(item);

  if (err) return <div className="dl-container py-20"><Link to="/radar/news" className="text-sm">← {t.nav.news}</Link><h1 className="font-display text-3xl mt-4">{lang === "fr" ? "Contenu introuvable" : "Content not found"}</h1><p className="mt-3 text-[#4B5563]">{lang === "fr" ? "Cette entrée est peut-être indisponible ou a changé d’adresse." : "This entry may be unavailable or may have moved."}</p></div>;
  if (!item) return <div className="dl-container py-20 text-[#4B5563]" role="status">{lang === "fr" ? "Chargement du contenu…" : "Loading content…"}</div>;

  return (
    <article data-testid="news-detail" className="dl-container py-10 max-w-4xl">
      <SeoHead
        lang={item.language || lang}
        {...articleSeoProps(item)}
        title={item.title}
        description={clampText(item.summary, 160) || `${item.relevance_area || "Supply Chain AI"} news - ${item.source || "Dataleo"}.`}
        type="article"
        image={item.image_url}
        publishedTime={item.published_at}
        modifiedTime={item.updated_at}
        jsonLd={buildArticleSchema({
          baseUrl: SITE_URL,
          item,
          type: "NewsArticle",
          path: item.canonical_path,
        })}
      />
      <Link to="/radar/news" className="inline-flex items-center gap-1 text-sm text-[#4B5563] hover:text-[#111827]">
        <ArrowLeft size={14} /> {t.nav.news}
      </Link>
      <ArticleLangNote item={item} lang={lang} />
      <div className="mt-6 flex flex-wrap items-center gap-2 text-[11px] font-mono-dl uppercase tracking-wider">
        <span className={`px-2 py-0.5 impact-${item.impact_level}`}>{t.labels.impact}: {t.impact[item.impact_level] || item.impact_level}</span>
        {item.relevance_area && <span className="text-[#00A878]">{item.relevance_area}</span>}
        <span className="text-[#9ca3af]"><Calendar size={12} className="inline mr-1" />{item.published_at}</span>
        <span className="text-[#4B5563]">· {item.source}</span>
      </div>
      <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl mt-3 leading-[1.05] text-[#111827]">{item.title}</h1>
      <HtmlText html={item.summary} className="html-text-block mt-4 text-[17px] text-[#4B5563] leading-relaxed" />
      <div className="mt-6 border border-[#E5E7EB] overflow-hidden bg-[#F3F4F6]">
        <SmartImage entity={item} type="news" alt={item.title || "News article cover"} className="w-full h-auto max-h-[420px]" />
      </div>
      {item.dataleo_angle && (
        <div className="mt-6 border-l-4 border-[#00A878] pl-5 py-2 bg-[#F7F7F2]">
          <div className="text-[11px] font-mono-dl uppercase tracking-wider text-[#00A878]">{t.labels.dataleoAngle}</div>
          <HtmlText html={item.dataleo_angle} className="html-text-block text-[16px] text-[#111827] mt-1 leading-relaxed" />
        </div>
      )}
      <div className="flex flex-wrap gap-2 mt-6">
        {(item.tags || []).map((tg) => (
          <TagChip key={tg} value={tg} />
        ))}
      </div>
      {item.url && (
        <div className="mt-8 pt-6 border-t border-[#E5E7EB]">
          <a
            href={item.url}
            target="_blank"
            rel="noreferrer"
            data-testid="news-source-link"
            className="inline-flex items-center gap-2 dl-btn-primary px-5 py-3 text-sm font-medium"
          >
            {t.sections.seeOriginal} <ExternalLink size={14} />
          </a>
        </div>
      )}
      <LinkedKeywords keywords={item.linked_keywords} lang={lang} testid="news-linked-keywords" />
    </article>
  );
};

export const JobDetail = () => {
  const { id } = useParams();
  const { t, lang } = useLang();
  const { item, err } = useArticle("jobs", id);

  if (err) return <div className="dl-container py-20"><Link to="/radar/jobs" className="text-sm">← {t.nav.jobs}</Link><h1 className="font-display text-3xl mt-4">{lang === "fr" ? "Contenu introuvable" : "Content not found"}</h1><p className="mt-3 text-[#4B5563]">{lang === "fr" ? "Cette entrée est peut-être indisponible ou a changé d’adresse." : "This entry may be unavailable or may have moved."}</p></div>;
  if (!item) return <div className="dl-container py-20 text-[#4B5563]" role="status">{lang === "fr" ? "Chargement du contenu…" : "Loading content…"}</div>;

  return (
    <article data-testid="job-detail" className="dl-container py-10 max-w-4xl">
      <SeoHead
        lang={item.language || lang}
        {...articleSeoProps(item)}
        title={[item.title, item.company].filter(Boolean).join(" - ")}
        description={clampText(item.summary, 160) || `${item.title}${item.company ? ` at ${item.company}` : ""}${item.location ? ` - ${item.location}` : ""}.`}
        type="article"
        image={item.company_logo}
        publishedTime={item.published_at}
        modifiedTime={item.updated_at}
        jsonLd={buildJobPostingSchema({ baseUrl: SITE_URL, item, path: item.canonical_path })}
      />
      <Link to="/radar/jobs" className="inline-flex items-center gap-1 text-sm text-[#4B5563] hover:text-[#111827]">
        <ArrowLeft size={14} /> {t.nav.jobs}
      </Link>
      <div className="mt-6 flex flex-wrap items-center gap-2 text-[11px] font-mono-dl uppercase tracking-wider">
        {item.work_mode && (
          <span data-testid="job-detail-workmode" className="px-2 py-0.5 bg-[#111827] text-white">{jobLabel("work_mode", item.work_mode, lang)}</span>
        )}
        {jobBadges(item, lang).map((b) => (
          <span key={b.field} data-testid={`job-detail-${b.field}`} className="text-[#4B5563]">· {b.label}</span>
        ))}
        <span className="ml-auto text-[#9ca3af]">{item.published_at}</span>
      </div>
      <div className="mt-3 flex items-start gap-4">
        <div className="w-16 h-16 sm:w-20 sm:h-20 shrink-0 border border-[#E5E7EB] bg-white p-2 grid place-items-center overflow-hidden">
          <SmartImage
            src={item.company_logo}
            type="job"
            alt={item.company || ""}
            fit="contain"
            className="max-w-[85%] max-h-[85%]"
          />
        </div>
        <div className="flex-1 min-w-0">
          <h1 className="font-display text-3xl sm:text-4xl leading-tight text-[#111827]">{item.title}</h1>
          <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-[14px] text-[#4B5563]">
            {item.company && <span className="inline-flex items-center gap-1.5"><Building2 size={14} /> {item.company}</span>}
            {item.location && <span className="inline-flex items-center gap-1.5"><MapPin size={14} /> {item.location}</span>}
            {item.function && <span className="font-mono-dl text-[12px]">{item.function}</span>}
          </div>
          <div data-testid="job-detail-salary" className="mt-2 text-[14px] font-mono-dl text-[#00A878]">
            {lang === "fr" ? "Salaire : " : "Salary: "}
            {hasSalary(item.salary_range)
              ? item.salary_range
              : (lang === "fr" ? "non communiqué" : "not disclosed")}
          </div>
          {item.technologies && item.technologies.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-3">
              {item.technologies.map((tech) => (
                <TechChip key={tech} name={tech} variant="soft" />
              ))}
            </div>
          )}
        </div>
      </div>
      {item.summary && <HtmlText html={item.summary} className="html-text-block mt-5 text-[16px] text-[#111827] leading-relaxed" />}
      {item.dataleo_angle && (
        <div className="mt-6 border-l-4 border-[#00A878] pl-5 py-2 bg-[#F7F7F2]">
          <div className="text-[11px] font-mono-dl uppercase tracking-wider text-[#00A878]">{t.labels.dataleoAngle}</div>
          <HtmlText html={item.dataleo_angle} className="html-text-block text-[15px] text-[#111827] mt-1 leading-relaxed" />
        </div>
      )}
      {item.url && (
        <div className="mt-8 pt-6 border-t border-[#E5E7EB]">
          <a href={item.url} target="_blank" rel="noreferrer" data-testid="job-source-link" className="inline-flex items-center gap-2 dl-btn-primary px-5 py-3 text-sm font-medium">
            {t.sections.apply} <ExternalLink size={14} />
          </a>
        </div>
      )}
      {(item.tags && item.tags.length > 0) && (
        <div data-testid="job-tags" className="flex flex-wrap gap-2 mt-8 pt-6 border-t border-[#E5E7EB]">
          {item.tags.map((tg) => <TagChip key={tg} value={tg} />)}
        </div>
      )}
      <LinkedKeywords keywords={item.linked_keywords} lang={lang} testid="job-linked-keywords" />
    </article>
  );
};

export const AlertDetail = () => {
  const { id } = useParams();
  const { t, lang } = useLang();
  const { item, err } = useArticle("alerts", id);
  useArticleLangRoutes(item);

  if (err) return <div className="dl-container py-20"><Link to="/radar/alerts" className="text-sm">← {t.nav.alerts}</Link><h1 className="font-display text-3xl mt-4">{lang === "fr" ? "Contenu introuvable" : "Content not found"}</h1><p className="mt-3 text-[#4B5563]">{lang === "fr" ? "Cette entrée est peut-être indisponible ou a changé d’adresse." : "This entry may be unavailable or may have moved."}</p></div>;
  if (!item) return <div className="dl-container py-20 text-[#4B5563]" role="status">{lang === "fr" ? "Chargement du contenu…" : "Loading content…"}</div>;

  const sevClass = { Info: "severity-info", Nomination: "severity-nomination", Indiscretion: "severity-indiscretion" };

  return (
    <article data-testid="alert-detail" className="dl-container py-10 max-w-4xl">
      <SeoHead
        lang={item.language || lang}
        {...articleSeoProps(item)}
        title={`${item.title}`}
        description={clampText(item.description, 160) || `${item.alert_type || "Alert"} - ${item.severity || ""}.`}
        type="article"
        publishedTime={item.published_at}
        modifiedTime={item.updated_at}
        jsonLd={buildArticleSchema({
          baseUrl: SITE_URL,
          item: { ...item, summary: item.description },
          type: "NewsArticle",
          path: item.canonical_path,
        })}
      />
      <Link to="/radar/alerts" className="inline-flex items-center gap-1 text-sm text-[#4B5563] hover:text-[#111827]">
        <ArrowLeft size={14} /> {t.nav.alerts}
      </Link>
      <ArticleLangNote item={item} lang={lang} />
      <div className="mt-6 flex flex-wrap items-center gap-2 text-[11px] font-mono-dl uppercase tracking-wider">
        <span className={`px-2 py-0.5 ${sevClass[item.severity]}`}>{t.severity[item.severity] || item.severity}</span>
        <span className="text-[#4B5563]">{item.alert_type}</span>
        <span className="text-[#9ca3af]">{item.published_at}</span>
      </div>
      <h1 className="font-display text-3xl sm:text-4xl mt-3 leading-tight text-[#111827] inline-flex items-start gap-2">
        <AlertTriangle size={28} className="mt-1.5 text-[#00A878]" />
        <span>{item.title}</span>
      </h1>
      <HtmlText html={item.description} className="html-text-block mt-5 text-[17px] text-[#111827] leading-relaxed" />
      {item.dataleo_angle && (
        <div className="mt-6 border-l-4 border-[#00A878] pl-5 py-2 bg-[#F7F7F2]">
          <div className="text-[11px] font-mono-dl uppercase tracking-wider text-[#00A878]">{t.labels.dataleoAngle}</div>
          <HtmlText html={item.dataleo_angle} className="html-text-block text-[15px] text-[#111827] mt-1 leading-relaxed" />
        </div>
      )}
      {item.url && (
        <div className="mt-8 pt-6 border-t border-[#E5E7EB]">
          <a href={item.url} target="_blank" rel="noreferrer" data-testid="alert-source-link" className="inline-flex items-center gap-2 dl-btn-primary px-5 py-3 text-sm font-medium">
            {t.sections.seeOriginal} <ExternalLink size={14} />
          </a>
        </div>
      )}
      {(item.tags && item.tags.length > 0) && (
        <div data-testid="alert-tags" className="flex flex-wrap gap-2 mt-8 pt-6 border-t border-[#E5E7EB]">
          {item.tags.map((tg) => <TagChip key={tg} value={tg} />)}
        </div>
      )}
      <LinkedKeywords keywords={item.linked_keywords} lang={lang} testid="alert-linked-keywords" />
    </article>
  );
};

export { resolveImg, pickEntityImage };
